import type { ConversationDetail } from '@chatbot/shared-types';
import type { ReactNode } from 'react';
import { ConversationActions } from './ConversationActions.tsx';
import { StateBadge } from './StateBadge.tsx';
import type { ConversationActionHandlers } from './useConversationActions.ts';

interface Props {
  conv: ConversationDetail | null;
  adminId: string;
  actions: ConversationActionHandlers;
  className?: string;
}

/**
 * Panel derecho: datos del contacto + acciones. Sólo muestra campos que la API
 * devuelve — no se inventan datos que el backend no tiene.
 */
export function ContextPanel({ conv, adminId, actions, className = '' }: Props) {
  const base = `flex-col min-h-0 border-l border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 ${className}`;

  if (!conv) {
    return (
      <aside aria-label="Contexto de la conversación" className={base}>
        <p className="p-4 text-caption text-neutral-500 dark:text-neutral-400">
          Sin conversación seleccionada.
        </p>
      </aside>
    );
  }

  const isOwnAdmin = conv.assignedAdminId === adminId;
  const lastActivity = conv.lastMessageAt ?? conv.stateChangedAt;

  return (
    <aside aria-label="Contexto de la conversación" className={`${base} overflow-y-auto`}>
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
        <h3 className="text-overline uppercase text-neutral-500 dark:text-neutral-400">Contacto</h3>
        <p className="mt-1 font-semibold text-neutral-900 dark:text-neutral-100 break-words">
          {conv.userDisplayName || conv.userIdentifier}
        </p>
      </div>

      <dl className="p-4 space-y-3 text-sm">
        <Field label="Canal">
          <span className="uppercase font-mono text-caption">{conv.channel}</span>
        </Field>
        <Field label="Identificador">
          <span className="font-mono break-all text-caption">{conv.userIdentifier}</span>
        </Field>
        <Field label="Estado">
          <StateBadge state={conv.state} />
        </Field>
        {conv.assignedAdminName && <Field label="Asignada a">{conv.assignedAdminName}</Field>}
        <Field label="Última actividad">
          <time dateTime={lastActivity}>{formatDateTime(lastActivity)}</time>
        </Field>
        <Field label="Mensajes">{conv.messages.length}</Field>
        <Field label="Tenant">
          <span className="font-mono text-caption break-all">{conv.tenantId}</span>
        </Field>
      </dl>

      <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 mt-auto">
        <h3 className="text-overline uppercase text-neutral-500 dark:text-neutral-400 mb-2">
          Acciones
        </h3>
        <ConversationActions
          state={conv.state}
          isOwnAdmin={isOwnAdmin}
          onTake={actions.onTake}
          onRelease={actions.onRelease}
          onResolve={actions.onResolve}
          onReturnToBot={actions.onReturnToBot}
        />
        {conv.state === 'bot_active' && (
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            El bot está a cargo. Cuando escale a un operador podrás tomarla.
          </p>
        )}
        {conv.state === 'resolved' && (
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            Conversación cerrada. Sin acciones disponibles.
          </p>
        )}
      </div>
    </aside>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-caption text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className="text-neutral-800 dark:text-neutral-200 mt-0.5">{children}</dd>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
